import { type Form, FormResponseStatus } from '@piccolohealth/pbs-common';
import { ChakraV3, createDownloadLink } from '@piccolohealth/ui';
import { Async, P } from '@piccolohealth/util';
import React from 'react';
import { createModal } from '../../../components/generic/Modal';
import { PiccoloError } from '../../../components/generic/PiccoloError';
import { useFormQuery } from '../../../graphql/hooks/useFormQuery';
import { useReprocessFormResponseMutation } from '../../../graphql/hooks/useReprocessFormResponseMutation';
import { useAppContext } from '../../../hooks/useAppContext';
import { FormResponseSummary } from './FormResponseSummary';
import { FormResponsesSidebar } from './FormResponsesSidebar';

interface FormResponsesModalContentProps {
  formId: string;
}

const FormResponsesModalContent = (props: FormResponsesModalContentProps) => {
  const { formId } = props;
  const { errorToast, successToast, organization } = useAppContext();
  const [activeResponseId, setActiveResponseId] = React.useState<string | null>(null);

  const { data, isLoading, error } = useFormQuery(
    {
      organizationId: organization.id,
      formId,
    },
    { refetchInterval: 5000 },
  );

  const form = data?.organization?.form as Form;
  const formResponses = P.take(form?.formResponses ?? [], 200);
  const latestResponse = P.first(formResponses);
  const activeResponse = formResponses.find((response) => response.id === activeResponseId);

  const mutation = useReprocessFormResponseMutation({
    onMutate: async () => {
      await Async.delay(2000);
    },
  });

  const handleReprocess = React.useCallback(
    async (formResponseId: string) => {
      await mutation
        .mutateAsync({
          organizationId: organization.id,
          formResponseId,
        })
        .then(async () => {
          successToast('Form response reprocessing started');
        })
        .catch((err) => {
          errorToast(`Error reprocessing form response: ${err.message}`);
        });
    },
    [mutation, organization.id, successToast, errorToast],
  );

  const handleDownload = React.useCallback((submissionId: string, response: unknown) => {
    const dataStr = JSON.stringify(response, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const filename = `form-response-${submissionId}.json`;
    createDownloadLink(blob, filename);
  }, []);

  React.useEffect(() => {
    if (!P.isNil(latestResponse)) {
      setActiveResponseId(latestResponse.id);
    }
  }, [latestResponse]);

  if (isLoading) {
    return (
      <ChakraV3.Center h='full'>
        <ChakraV3.Spinner />
      </ChakraV3.Center>
    );
  }

  if (error) {
    return <PiccoloError error={error} />;
  }

  return (
    <ChakraV3.HStack w='full' h='full' gap={4}>
      <FormResponsesSidebar
        formResponses={formResponses}
        activeResponseId={activeResponseId}
        setActiveResponseId={setActiveResponseId}
      />

      {activeResponse && (
        <ChakraV3.Stack w='full' h='full' align='center'>
          <ChakraV3.Box h='full' w='full' overflowY='auto' layerStyle='fade'>
            <FormResponseSummary formResponse={activeResponse} />
          </ChakraV3.Box>
          {activeResponse.errorMessage && activeResponse.status === FormResponseStatus.Failed && (
            <ChakraV3.AlertBanner
              status='error'
              alignItems='center'
              fontSize='md'
              title='Processing Error'
            >
              <ChakraV3.Text fontSize='sm'>{activeResponse.errorMessage}</ChakraV3.Text>
            </ChakraV3.AlertBanner>
          )}

          <ChakraV3.AlertBanner
            status='neutral'
            alignItems='center'
            fontSize='md'
            title='Reprocess or download form response'
            endElement={
              <ChakraV3.HStack>
                <ChakraV3.Button
                  size='sm'
                  onClick={() =>
                    handleDownload(activeResponse.submissionId, activeResponse.response)
                  }
                  variant='subtle'
                >
                  Download
                </ChakraV3.Button>
                <ChakraV3.Button
                  onClick={() => handleReprocess(activeResponse.id)}
                  colorPalette='brand'
                  size='sm'
                  loading={
                    mutation.isLoading || activeResponse.status === FormResponseStatus.Processing
                  }
                  loadingText='Processing...'
                  disabled={activeResponse.status !== FormResponseStatus.Failed}
                >
                  Reprocess
                </ChakraV3.Button>
              </ChakraV3.HStack>
            }
          />
        </ChakraV3.Stack>
      )}
    </ChakraV3.HStack>
  );
};

interface FormResponsesModalProps {
  formId: string;
  formName: string;
}

export const FormResponsesModal = createModal<FormResponsesModalProps>((props) => {
  const { formId, formName, modal } = props;
  const { hide, remove, visible } = modal;

  return (
    <ChakraV3.Dialog.Root
      open={visible}
      onOpenChange={hide}
      onExitComplete={remove}
      size='cover'
      scrollBehavior='inside'
    >
      <ChakraV3.Dialog.Backdrop />
      <ChakraV3.Dialog.Positioner>
        <ChakraV3.Dialog.Content>
          <ChakraV3.DialogTitleSubtitle
            title={`Form Responses - ${formName}`}
            subtitle='View and manage form responses'
          />
          <ChakraV3.Dialog.Body>
            <FormResponsesModalContent formId={formId} />
          </ChakraV3.Dialog.Body>
        </ChakraV3.Dialog.Content>
      </ChakraV3.Dialog.Positioner>
    </ChakraV3.Dialog.Root>
  );
});
